import React, { useState, useEffect, useRef } from 'react';
import { bool, func, object } from 'prop-types';
import { useHistory } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  TextField,
  Typography,
  Chip,
  Tooltip,
  Card,
  CircularProgress,
  Divider,
  Switch,
  FormControlLabel,
  Alert,
  AlertTitle,
  useMediaQuery,
  useTheme
} from '@mui/material';
import cx from 'classnames';
import {
  SmartToy as RobotIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Send as SendIcon,
  Close as CloseIcon,
  CheckCircleOutline,
  AttachMoney,
  WarningAmber,
  AddShoppingCart,
  SendAndArchive,
  DeleteOutline
} from '@mui/icons-material';
import { Row, Column } from 'simple-flexbox';
import { formatCurrency, EnumClientStatus } from '@eeff/shared';
import {
  sendAgentMessage,
  createSellRequestFromAgent
  // fetchRecurringSuggestions
} from '../../../services/agentService';
import { setSavedSell } from '../../../services/localStorageService';
import {
  showInfoMessage,
  showErrorMessage
} from '../../../utils/common-components/ToastNotificationEmitter';
import { PRIVATE_BASE_PATH } from '../../../utils/globals';
import Loading from '../../../utils/common-components/UIBlocker/Loading';
import classes from './AiSellAssistantModal.module.scss';

const VOICE_TRANSCRIPT_CORRECTIONS = [
  // Client names mis-transcribed by es-AR SpeechRecognition
  [
    /\b(?:los\s+ciegos|lo\s+ciego|el\s+ciego|los\s+fiegos|lo\s+fiegos|los\s+fiego|lo\s+fiejo|los\s+fiejos|lofiego)\b/gi,
    'Lo Fiego'
  ],
  [/\b(?:caicen|caisen|kaisen)\b/gi, 'Kaizen'],
  [/\b(?:la\s+coya|lacolla|lacoya)\b/gi, 'La Colla'],
  [/\b(?:dinardo)\b/gi, 'Di Nardo'],
  [/\b(?:demonte)\b/gi, 'De Monte'],
  // English product names in DB phoneticized by es-AR SpeechRecognition
  [
    /\b(?:chein|chain|shain|chan|chen|cheyn|chey|chay)\s*(?:lub|lube|luv|club|luz|lo|lu)\b/gi,
    'chain lube'
  ],
  [/\b(?:chailub|chailu|cheinlub|chainlub)\b/gi, 'chain lube'],
  [/\b(?:brake|break|breik|braik|brek|brik|brey|bray)\s+(?:oil|oyl)\b/gi, 'brake oil'],
  [/\b(?:brake|break|breik|braik|brek|brik|brey|bray)\s+(?:fluid|fluir|flui)\b/gi, 'brake fluid'],
  [/\b(?:filter)\s+(?:oil|oyl|hoy)\b/gi, 'filter oil'],
  [/\b(?:guir|giar|geer|gear|guer)\s+(?:oil|oyl)\b/gi, 'gear oil'],
  [/\b(?:sin\s*gear|singear|sin\s*guir|singuir|syn\s*gear)\b/gi, 'syngear'],
  [/\b(?:fork|for|forc)\s+(?:tuner|taner|tener|tunner)\b/gi, 'fork tuner'],
  [/\b(?:super)\s*(?:cliner|clener|cleaner|kliner)\b/gi, 'super cleaner'],
  [/\b(?:clin|clean|klin)\s+(?:oranch|orange|oranje)\b/gi, 'clean orange'],
  [/\b(?:ama)\s+(?:clin|clean|klean)\b/gi, 'ama clean'],
  [/\b(?:water|guater|wuater|woter)\s*(?:pruf|proof|prouf)\b/gi, 'water proof'],
  [/\b(?:ful|full)\s+(?:sintetic|synthetic)\b/gi, 'full synthetic'],
  [/\b(?:semi)\s*(?:sintetic|synthetic)\b/gi, 'semisynthetic'],
  [/\b(?:ultra)\s*(?:sint|synth|sin)\b/gi, 'ultrasynth'],
  [/\b(?:power|pauer|paguer|pawer)\s*(?:trac|track|trak)\b/gi, 'power trac'],
  [/\b(?:sin|syn)\s*(?:trac|track|trak)\b/gi, 'syntrac'],
  [/\b(?:flat|flan)\s+(?:fri|free)\b/gi, 'flat free'],
  [/\b(?:ol|all)\s+in\s+(?:guan|wan|one)\b/gi, 'all in one'],
  [/\b(?:energi|energy)\s+(?:rilis|release|relis)\b/gi, 'energy release'],
  [/\b(?:biring|bering|bearing)\s+(?:rilis|release|relis)\b/gi, 'bearing release'],
  [/\b(?:spid|speed|espid)\s+(?:biring|bering|bearing)\b/gi, 'speed bearing'],
  [/\b(?:anti\s*friz|antifriz|anti\s*fris|antifris|anti\s*freeze)\b/gi, 'antifreeze'],
  [/\b(?:culant|culan|kulan|coolan)\b/gi, 'coolant'],
  [/\b(?:muli\s+g|multig)\b/gi, 'multi g'],
  [/\b(?:formulag)\b/gi, 'formula g'],
  // Spoken viscosity patterns (e.g. "10 doble ve 40", "10 w 40", "15 uve doble 40")
  [
    /\b(0|5|10|15|20|25|75|80|85)\s*(?:doble\s*ve?|uve\s*doble|w)\s*(20|30|40|50|60|80|90|140)\b/gi,
    '$1w$2'
  ]
];

export const normalizeVoiceTranscript = (text) => {
  if (!text || typeof text !== 'string') return '';
  let result = text;
  for (const [pattern, replacement] of VOICE_TRANSCRIPT_CORRECTIONS) {
    result = result.replace(pattern, replacement);
  }
  return result;
};

const AiSellAssistantModal = ({
  open,
  onClose,
  onDraftHydrate,
  opesi = false,
  isQuoteRequest = false,
  opesiTaxPercentageSelected = opesi && isQuoteRequest ? 0 : 1
}) => {
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [autoProcess, setAutoProcess] = useState(false);
  const [threadId, setThreadId] = useState(null);

  // const [suggestions, setSuggestions] = useState([]);
  const [assistantReply, setAssistantReply] = useState('');
  const [draft, setDraft] = useState(null);
  const [ambiguities, setAmbiguities] = useState(null);
  const [isCashPayment, setIsCashPayment] = useState(false);
  const [removedItems, setRemovedItems] = useState([]);

  const recognitionRef = useRef(null);
  const isRecordingRef = useRef(false);
  const baseTextRef = useRef('');
  const currentTextRef = useRef('');
  const silenceTimerRef = useRef(null);

  // Pointer hold-to-speak refs (WhatsApp style)
  const pointerDownTimeRef = useRef(0);
  const isHoldingRef = useRef(false);
  const holdTimerRef = useRef(null);
  const pendingAutoProcessRef = useRef(false);
  const autoProcessTimerRef = useRef(null);

  const SILENCE_TIMEOUT_MS = 3000;

  useEffect(() => {
    currentTextRef.current = inputText;
  }, [inputText]);

  const clearSilenceTimer = () => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  };

  const clearAutoProcessTimer = () => {
    pendingAutoProcessRef.current = false;
    if (autoProcessTimerRef.current) {
      clearTimeout(autoProcessTimerRef.current);
      autoProcessTimerRef.current = null;
    }
  };

  const resetSilenceTimer = () => {
    clearSilenceTimer();
    if (isRecordingRef.current) {
      silenceTimerRef.current = setTimeout(() => {
        if (isRecordingRef.current) {
          stopRecording();
        }
      }, SILENCE_TIMEOUT_MS);
    }
  };

  const stopRecording = () => {
    clearSilenceTimer();
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    isHoldingRef.current = false;
    isRecordingRef.current = false;
    setIsRecording(false);
    if (recognitionRef.current) {
      const activeRecognition = recognitionRef.current;
      recognitionRef.current = null;
      try {
        activeRecognition.stop();
      } catch (e) {
        try {
          activeRecognition.abort();
        } catch (e2) {
          // ignore
        }
      }
    }
  };

  const resetModalState = () => {
    clearAutoProcessTimer();
    stopRecording();
    setInputText('');
    currentTextRef.current = '';
    baseTextRef.current = '';
    setAssistantReply('');
    setDraft(null);
    setAmbiguities(null);
    setIsCashPayment(false);
    setLoading(false);
    setActionLoading(false);
    setThreadId(null);
    setRemovedItems([]);
  };

  // Cleanup speech recognition on unmount
  useEffect(() => {
    return () => {
      stopRecording();
    };
  }, []);

  const startRecognitionSession = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'es-AR';

      recognition.onstart = () => {
        setIsRecording(true);
      };

      recognition.onspeechstart = () => {
        resetSilenceTimer();
      };

      recognition.onresult = (event) => {
        resetSilenceTimer();
        let sessionTranscript = '';
        for (let i = 0; i < event.results.length; i++) {
          sessionTranscript += event.results[i][0].transcript;
        }
        const normalizedSession = normalizeVoiceTranscript(sessionTranscript);
        const prefix = baseTextRef.current ? `${baseTextRef.current.trim()} ` : '';
        const updated = `${prefix}${normalizedSession}`;
        setInputText(updated);
        currentTextRef.current = updated;
      };

      recognition.onerror = (event) => {
        console.warn('[AiSellAssistant] Speech recognition error:', event.error);
        if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
          stopRecording();
          showErrorMessage(
            'Permiso de micrófono denegado. Habilita el acceso al micrófono en el navegador.'
          );
        } else if (event.error === 'network') {
          stopRecording();
          showErrorMessage('Error de red al conectar con el servicio de voz.');
        } else if (event.error !== 'no-speech' && event.error !== 'aborted') {
          stopRecording();
          showErrorMessage(`Error de voz: ${event.error}`);
        }
      };

      recognition.onend = () => {
        // If user didn't stop recording and silence timeout hasn't elapsed, restart session to maintain continuous dictation
        if (isRecordingRef.current) {
          baseTextRef.current = currentTextRef.current;
          setTimeout(() => {
            if (isRecordingRef.current) {
              try {
                startRecognitionSession();
              } catch (e) {
                setTimeout(() => {
                  if (isRecordingRef.current) {
                    try {
                      startRecognitionSession();
                    } catch (e2) {
                      stopRecording();
                    }
                  }
                }, 150);
              }
            }
          }, 50);
        } else {
          setIsRecording(false);
          recognitionRef.current = null;

          if (pendingAutoProcessRef.current) {
            clearAutoProcessTimer();
            // Wait 500ms after final speech chunk is settled so the user sees the complete text in the textfield
            setTimeout(() => {
              const text = (currentTextRef.current || '').trim();
              if (text) {
                handleSendMessage(text);
              }
            }, 500);
          }
        }
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err) {
      console.error('[AiSellAssistant] Failed to start speech recognition session:', err);
      stopRecording();
      showErrorMessage('No se pudo iniciar el reconocimiento de voz.');
    }
  };

  const startRecording = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      showErrorMessage(
        'El reconocimiento de voz no está soportado en este navegador. Usa Chrome o Edge.'
      );
      return;
    }

    if (isRecordingRef.current) return;

    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
      } catch (e) {
        // ignore
      }
      recognitionRef.current = null;
    }

    isRecordingRef.current = true;
    setIsRecording(true);
    baseTextRef.current = inputText || '';
    resetSilenceTimer();
    startRecognitionSession();
  };

  const handlePointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;

    pointerDownTimeRef.current = Date.now();
    isHoldingRef.current = false;

    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
    }

    holdTimerRef.current = setTimeout(() => {
      isHoldingRef.current = true;
      if (!isRecordingRef.current) {
        startRecording();
      }
    }, 250);
  };

  const handlePointerUp = () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }

    if (isHoldingRef.current) {
      // It was a HOLD gesture: releasing stops recording immediately
      isHoldingRef.current = false;
      const shouldAuto = autoProcess;

      if (shouldAuto) {
        pendingAutoProcessRef.current = true;
        // Fallback timer in case onend takes longer or doesn't fire
        if (autoProcessTimerRef.current) {
          clearTimeout(autoProcessTimerRef.current);
        }
        autoProcessTimerRef.current = setTimeout(() => {
          if (pendingAutoProcessRef.current) {
            pendingAutoProcessRef.current = false;
            const text = (currentTextRef.current || '').trim();
            if (text) {
              handleSendMessage(text);
            }
          }
        }, 1200);
      }

      stopRecording();
    } else {
      // It was a quick CLICK (< 250ms): toggle hands-free recording
      if (isRecordingRef.current) {
        stopRecording();
      } else {
        startRecording();
      }
    }
  };

  const handlePointerCancel = () => {
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (isHoldingRef.current) {
      isHoldingRef.current = false;
      stopRecording();
    }
  };

  // Reset all state when modal closes
  useEffect(() => {
    if (!open) {
      resetModalState();
    }
  }, [open]);

  const handleSendMessage = async (textToSend) => {
    const message = (textToSend || inputText).trim();
    if (!message) return;

    setLoading(true);
    setAmbiguities(null);

    try {
      const currentDraftPayload = draft
        ? {
            clientId: draft.client?.id,
            clientName: draft.client?.fantasyName || draft.client?.fullName,
            isCashPayment,
            items: (draft.items || []).map((it) => ({
              productId: it.rawProductId || it.productId,
              promoId: it.rawPromoId || it.promoId,
              productName: it.productName || it.name,
              quantity: it.quantity,
              unitPrice: it.unitPrice,
              sellPriceNoTaxes: it.sellPriceNoTaxes,
              discount: it.discount,
              dirtyPrice: it.dirtyPrice
            })),
            removedItems
          }
        : null;

      const res = await sendAgentMessage({
        message,
        threadId,
        opesi,
        isQuoteRequest,
        opesiTaxPercentageSelected,
        currentDraft: currentDraftPayload
      });

      if (res.threadId) {
        setThreadId(res.threadId);
      }

      const fallbackReply =
        res.reply ||
        (res.draft
          ? 'Pedido preparado con éxito.'
          : res.ambiguities && res.ambiguities.length > 0
            ? 'Se encontraron varios clientes posibles. Selecciona el indicado:'
            : 'No se pudo interpretar el pedido. Por favor intenta especificando cliente y productos.');

      setAssistantReply(fallbackReply);
      setIsCashPayment(Boolean(res.isCashPayment));

      if (res.draft) {
        setDraft(res.draft);
        setInputText('');
      } else if (res.ambiguities && res.ambiguities.length > 0) {
        setAmbiguities(res.ambiguities);
      }
    } catch (err) {
      setAssistantReply(err.message || 'Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAmbiguousClient = (client) => {
    const prompt = `Selecciono la cuenta #${client.id} (${client.name || client.fantasyName})`;
    handleSendMessage(prompt);
  };

  const handleRemoveDraftItem = (itemIndex) => {
    if (!draft || !draft.items) return;

    const removedItem = draft.items[itemIndex];
    if (removedItem) {
      setRemovedItems((prev) => [
        ...prev,
        {
          productId: removedItem.rawProductId || removedItem.productId || null,
          promoId: removedItem.rawPromoId || removedItem.promoId || null,
          name: removedItem.productName || removedItem.name || '',
          quantity: removedItem.quantity || 1,
          price: removedItem.unitPrice || removedItem.sellPriceNoTaxes || 0
        }
      ]);
    }
    const newItems = draft.items.filter((_, idx) => idx !== itemIndex);

    if (newItems.length === 0) {
      setDraft(null);
      setAssistantReply('Se eliminaron todos los artículos del borrador.');
      showInfoMessage(
        `Artículo "${removedItem?.productName || removedItem?.name || ''}" eliminado. El borrador quedó vacío.`
      );
      return;
    }

    // Recalculate totals
    const newTotalNet = Number(
      newItems
        .reduce(
          (acc, it) => acc + (it.unitPriceNoDisc || it.sellPriceNoTaxes || 0) * (it.quantity || 1),
          0
        )
        .toFixed(2)
    );
    const newTotalTaxes = Number(
      newItems.reduce((acc, it) => acc + (it.totalTaxes || 0), 0).toFixed(2)
    );
    const newTotalDiscounted = Number(
      newItems.reduce((acc, it) => acc + (it.totalDiscounted || 0), 0).toFixed(2)
    );
    const newTotalWithDiscount = Number(
      newItems.reduce((acc, it) => acc + (it.total || 0), 0).toFixed(2)
    );

    const hasStockWarnings = newItems.some((it) => !it.hasEnoughStock);
    const stockWarnings = hasStockWarnings
      ? (draft.summary?.stockWarnings || []).filter(
          (w) => w.productId !== removedItem?.rawProductId && w.productId !== removedItem?.productId
        )
      : [];

    const paymentData = isCashPayment
      ? {
          paymentDetails: [
            {
              id: 0,
              type: 'CASH',
              amount: newTotalWithDiscount
            }
          ]
        }
      : null;

    const newSummary = {
      ...(draft.summary || {}),
      totalNet: newTotalNet,
      totalTaxes: newTotalTaxes,
      totalDiscounted: newTotalDiscounted,
      totalWithDiscount: newTotalWithDiscount,
      hasStockWarnings,
      stockWarnings,
      paymentData
    };

    const newSellRequestPayload = draft.sellRequestPayload
      ? {
          ...draft.sellRequestPayload,
          sellItems: (draft.sellRequestPayload.sellItems || []).filter(
            (_, idx) => idx !== itemIndex
          )
        }
      : null;

    const newSellPayload = draft.sellPayload
      ? {
          ...draft.sellPayload,
          totalNet: newTotalNet,
          totalTaxes: newTotalTaxes,
          totalDiscounted: newTotalDiscounted,
          totalWithDiscount: newTotalWithDiscount,
          sellItems: (draft.sellPayload.sellItems || []).filter((_, idx) => idx !== itemIndex)
        }
      : null;

    const newFeState = draft.feState
      ? {
          ...draft.feState,
          items: newItems,
          totalWithDiscount: newTotalWithDiscount,
          totalNet: newTotalNet,
          totalSellTaxes: newTotalTaxes,
          totalSellDiscounted: newTotalDiscounted,
          paymentData
        }
      : null;

    setDraft({
      ...draft,
      items: newItems,
      summary: newSummary,
      sellRequestPayload: newSellRequestPayload,
      sellPayload: newSellPayload,
      feState: newFeState,
      paymentData
    });

    showInfoMessage(
      `Artículo "${removedItem?.productName || removedItem?.name || ''}" eliminado del borrador.`
    );
  };

  const handleSaveAsSellRequest = async () => {
    if (!draft || !draft.sellRequestPayload) return;

    setActionLoading(true);
    try {
      const res = await createSellRequestFromAgent({
        sellRequestPayload: draft.sellRequestPayload,
        auditId: draft.auditId || draft.sellRequestPayload?.auditId,
        threadId,
        removedItems
      });

      showInfoMessage(
        `¡Pedido de venta #${res.sellRequest?.orderNumber || res.sellRequest?.id} guardado con éxito!`
      );
      handleClose();
    } catch (err) {
      showErrorMessage(err.message || 'Error al guardar el pedido de venta');
    } finally {
      setActionLoading(false);
    }
  };

  const handleHydrateForm = () => {
    if (!draft || !draft.feState) return;

    const feStateToHydrate = {
      ...draft.feState,
      auditId: draft.auditId || draft.feState?.auditId || draft.sellPayload?.auditId || null,
      removedItems
    };

    if (onDraftHydrate) {
      onDraftHydrate(feStateToHydrate);
      showInfoMessage('Venta cargada en el formulario.');
      handleClose();
    } else {
      setSavedSell(feStateToHydrate);
      showInfoMessage('Borrador listo. Abriendo formulario de venta...');
      handleClose();
      history.push(`${PRIVATE_BASE_PATH}sells/create`, { restoreDraft: true });
    }
  };

  const handleClose = (event, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    resetModalState();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
    >
      <DialogTitle className={classes.dialogTitle}>
        <div className={classes.headerTitleContainer}>
          <RobotIcon color="primary" className={classes.headerRobotIcon} />
          <Typography id="ai-assistant-modal-title" variant="h6" className={classes.headerTitle}>
            Asistente AI - Venta
          </Typography>
        </div>
        <IconButton size="small" onClick={handleClose} className={classes.closeButton}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {loading ? (
          <Loading loading={loading} />
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" className={classes.instructionsText}>
              Dicta o escribe el pedido tal como te lo pasa el cliente:
              <i className={classes.instructionsExample}> &quot;Gomez cuenta 4 quiere...&quot;</i>
            </Typography>

            {/* Ambiguities / Candidate selection list */}
            {ambiguities && ambiguities.length > 0 && (
              <Card className={classes.ambiguitiesCard}>
                <Typography variant="subtitle2" className={classes.ambiguitiesTitle}>
                  ⚠️ Clientes coincidentes encontrados. Selecciona cuál corresponde:
                </Typography>
                <Column className={classes.ambiguitiesList}>
                  {ambiguities.map((c) => (
                    <Row
                      key={c.id}
                      vertical="center"
                      horizontal="spaced"
                      className={classes.ambiguityItem}
                    >
                      <Column>
                        <Row vertical="center" style={{ gap: 8 }}>
                          <Typography variant="body2" className={classes.ambiguityItemTitle}>
                            Cuenta #{c.id} -{' '}
                            {c.fantasyName ? `${c.fantasyName} (${c.name})` : c.name}
                          </Typography>
                          {c.clientStatus === EnumClientStatus.BANNED && (
                            <Chip
                              label="BLOQUEADO"
                              color="error"
                              size="small"
                              variant="outlined"
                              className={classes.ambiguityBannedChip}
                            />
                          )}
                          {c.clientStatus === EnumClientStatus.DELETED && (
                            <Chip
                              label="ELIMINADO"
                              color="error"
                              size="small"
                              variant="outlined"
                              className={classes.ambiguityBannedChip}
                            />
                          )}
                          {c.clientStatus === EnumClientStatus.REJECTED && (
                            <Chip
                              label="RECHAZADO"
                              color="error"
                              size="small"
                              variant="outlined"
                              className={classes.ambiguityBannedChip}
                            />
                          )}
                          {c.clientStatus === EnumClientStatus.DEACTIVATED && (
                            <Chip
                              label="INACTIVO"
                              color="warning"
                              size="small"
                              variant="outlined"
                              className={classes.ambiguityDeactivatedChip}
                            />
                          )}
                        </Row>
                        <Typography variant="caption" color="textSecondary">
                          {c.taxId ? `CUIT: ${c.taxId} • ` : ''}
                          {c.city ? `Ciudad: ${c.city} • ` : ''}
                          {c.phones && c.phones.length > 0 ? `Tel: ${c.phones.join(', ')}` : ''}
                        </Typography>
                      </Column>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => handleSelectAmbiguousClient(c)}
                      >
                        Elegir
                      </Button>
                    </Row>
                  ))}
                </Column>
              </Card>
            )}

            {/* Agent response text (only shown when no draft is prepared yet, e.g. questions or clarifications) */}
            {!draft && assistantReply && (
              <Card className={classes.assistantReplyCard}>
                <Typography variant="body2" className={classes.assistantReplyText}>
                  {assistantReply}
                </Typography>
              </Card>
            )}

            {/* Prepared Draft Preview Card */}
            {draft && (
              <Card className={classes.draftCard}>
                <Column className={classes.draftHeaderColumn}>
                  {draft.client?.clientStatus === EnumClientStatus.BANNED && (
                    <Alert
                      severity="error"
                      icon={<WarningAmber fontSize="inherit" />}
                      className={classes.bannedClientAlert}
                    >
                      <AlertTitle>
                        <strong>Cliente Bloqueado</strong>
                      </AlertTitle>
                      ¿Deseas proceder de todos modos con el pedido?
                    </Alert>
                  )}
                  {draft.client?.clientStatus === EnumClientStatus.DELETED && (
                    <Alert
                      severity="error"
                      icon={<WarningAmber fontSize="inherit" />}
                      className={classes.bannedClientAlert}
                    >
                      <AlertTitle>
                        <strong>Cliente Eliminado</strong>
                      </AlertTitle>
                      Si confirmas este pedido, se guardará como Nota de venta pendiente para que un
                      administrador revise y reactive la cuenta existente.
                    </Alert>
                  )}
                  {draft.client?.clientStatus === EnumClientStatus.REJECTED && (
                    <Alert
                      severity="error"
                      icon={<WarningAmber fontSize="inherit" />}
                      className={classes.bannedClientAlert}
                    >
                      <AlertTitle>
                        <strong>Cliente Rechazado</strong>
                      </AlertTitle>
                      Si confirmas este pedido, se guardará como Nota de venta pendiente para
                      revisión administrativa.
                    </Alert>
                  )}

                  <Row vertical="center" className={classes.draftClientTitleRow}>
                    <CheckCircleOutline color="success" className={classes.successIcon} />
                    <Typography variant="subtitle1" className={classes.draftClientName}>
                      {draft.client?.fantasyName || draft.client?.fullName} (Cuenta #
                      {draft.client?.id})
                    </Typography>
                  </Row>

                  <Row vertical="center" className={classes.draftBadgesRow}>
                    {isCashPayment ? (
                      <Chip
                        icon={<AttachMoney style={{ fontSize: 16 }} />}
                        label="Pago al Contado"
                        color="success"
                        size="small"
                        className={classes.cashPaymentChip}
                      />
                    ) : (
                      <Chip
                        label="Cuenta Corriente"
                        size="small"
                        variant="outlined"
                        className={classes.paymentTypeChip}
                      />
                    )}
                    {draft.client?.clientStatus === EnumClientStatus.PENDING_APPROVAL && (
                      <Chip
                        label="Cliente Nuevo"
                        color="warning"
                        size="small"
                        variant="outlined"
                        className={classes.pendingClientChip}
                      />
                    )}
                    {draft.client?.clientStatus === EnumClientStatus.DEACTIVATED && (
                      <Chip
                        label="Cliente Inactivo (Se reactivará al crear la venta)"
                        color="warning"
                        size="small"
                        variant="outlined"
                        className={classes.deactivatedClientChip}
                      />
                    )}
                    {draft.client?.clientStatus === EnumClientStatus.BANNED && (
                      <Chip
                        label="Cliente Bloqueado"
                        color="error"
                        size="small"
                        className={classes.bannedClientChip}
                      />
                    )}
                    {draft.client?.clientStatus === EnumClientStatus.DELETED && (
                      <Chip
                        label="Cliente Eliminado"
                        color="error"
                        size="small"
                        className={classes.bannedClientChip}
                      />
                    )}
                    {draft.client?.clientStatus === EnumClientStatus.REJECTED && (
                      <Chip
                        label="Cliente Rechazado"
                        color="error"
                        size="small"
                        className={classes.bannedClientChip}
                      />
                    )}
                  </Row>

                  {draft.missingItems && draft.missingItems.length > 0 && (
                    <Alert severity="warning" className={classes.missingItemsAlert}>
                      <AlertTitle>
                        <strong>Artículos no encontrados ({draft.missingItems.length})</strong>
                      </AlertTitle>
                      <Typography variant="body2" component="div">
                        No se encontraron coincidencias en el catálogo para:
                        <ul className={classes.missingItemsList}>
                          {draft.missingItems.map((itemStr, miIdx) => (
                            <li key={miIdx} className={classes.missingItemRow}>
                              <strong>{itemStr}</strong>
                            </li>
                          ))}
                        </ul>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          className={classes.missingItemsHint}
                        >
                          Puedes continuar con el resto de la orden o solicitar agregarlo con otro
                          nombre.
                        </Typography>
                      </Typography>
                    </Alert>
                  )}
                </Column>

                {/* Mobile-friendly item cards */}
                <div className={classes.draftItemsList}>
                  {(draft.items || []).map((item, idx) => (
                    <div
                      key={idx}
                      className={cx(classes.draftItemCard, {
                        [classes.draftItemWarning]: !item.hasEnoughStock
                      })}
                    >
                      <div className={classes.draftItemTopRow}>
                        <Typography variant="body2" className={classes.draftItemName}>
                          {item.productName || item.name}
                        </Typography>
                        <div className={classes.draftItemPriceAndAction}>
                          <Typography variant="subtitle2" className={classes.draftItemTotal}>
                            {formatCurrency(item.total)}
                          </Typography>
                          <Tooltip title="Eliminar artículo del pedido">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveDraftItem(idx)}
                              className={classes.deleteItemButton}
                              aria-label="Eliminar artículo"
                            >
                              <DeleteOutline fontSize="small" className={classes.deleteItemIcon} />
                            </IconButton>
                          </Tooltip>
                        </div>
                      </div>

                      <div className={classes.draftItemBottomRow}>
                        <div className={classes.draftItemQuantityPricing}>
                          <span className={classes.quantityBadge}>{item.quantity} u.</span>
                          <span className={classes.unitPriceLabel}>
                            {' × '}
                            {formatCurrency(item.unitPrice)}
                          </span>
                        </div>

                        {!item.hasEnoughStock && (
                          <Tooltip
                            title={`Stock disponible en depósito: ${item.availableStock ?? 0} u.`}
                          >
                            <Chip
                              icon={<WarningAmber style={{ fontSize: 13 }} />}
                              label={
                                item.availableStock !== undefined
                                  ? `Stock: ${item.availableStock} u.`
                                  : 'Stock insuficiente'
                              }
                              color="warning"
                              size="small"
                              className={classes.stockWarningChip}
                            />
                          </Tooltip>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                <Divider className={classes.draftDivider} />

                <Row horizontal="end" style={{ width: '100%' }}>
                  <Column className={classes.draftSummaryColumn}>
                    <Row horizontal="spaced">
                      <Typography variant="caption">Neto:</Typography>
                      <Typography variant="caption">
                        {formatCurrency(draft.summary?.totalNet)}
                      </Typography>
                    </Row>
                    {Boolean(draft.summary?.totalDiscounted) && (
                      <Row horizontal="spaced">
                        <Typography variant="caption">Descuento:</Typography>
                        <Typography variant="caption" color="secondary">
                          -{formatCurrency(draft.summary?.totalDiscounted)}
                        </Typography>
                      </Row>
                    )}
                    <Row horizontal="spaced">
                      <Typography variant="caption">IVA / Impuestos:</Typography>
                      <Typography variant="caption">
                        {formatCurrency(draft.summary?.totalTaxes)}
                      </Typography>
                    </Row>
                    <Row horizontal="spaced" className={classes.draftTotalRow}>
                      <Typography variant="subtitle2" className={classes.draftTotalLabel}>
                        TOTAL:
                      </Typography>
                      <Typography variant="subtitle2" className={classes.draftTotalValue}>
                        {formatCurrency(draft.summary?.totalWithDiscount)}
                      </Typography>
                    </Row>
                  </Column>
                </Row>

                {/* Street & Desktop Action buttons */}
                {(() => {
                  const isBlockedClient =
                    draft.client?.clientStatus === EnumClientStatus.DELETED ||
                    draft.client?.clientStatus === EnumClientStatus.REJECTED;

                  return (
                    <div className={classes.draftActionsRow}>
                      <Tooltip
                        title={
                          isBlockedClient
                            ? 'Nota: La cuenta figura como eliminada/rechazada y requerirá reactivación administrativa.'
                            : ''
                        }
                      >
                        <span>
                          <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<SendAndArchive />}
                            onClick={handleSaveAsSellRequest}
                            disabled={actionLoading}
                            className={classes.draftActionButton}
                          >
                            {actionLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              'Guardar como Pedido (Calle)'
                            )}
                          </Button>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          isBlockedClient
                            ? 'Nota: La cuenta figura como eliminada/rechazada y requerirá reactivación administrativa.'
                            : ''
                        }
                      >
                        <span>
                          <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<AddShoppingCart />}
                            onClick={handleHydrateForm}
                            disabled={actionLoading}
                            className={classes.draftActionButton}
                          >
                            Cargar en Formulario
                          </Button>
                        </span>
                      </Tooltip>
                    </div>
                  );
                })()}
              </Card>
            )}

            {/* Input Controls */}
            <Row
              vertical="center"
              spaceBetween
              className={classes.inputHeaderRow}
              style={{ flexWrap: 'wrap', gap: 10 }}
            >
              <Row vertical="center" style={{ gap: 10, flexWrap: 'wrap' }}>
                <Button
                  id="ai-assistant-voice-btn"
                  variant="contained"
                  size="small"
                  color={isRecording ? 'error' : 'info'}
                  startIcon={isRecording ? <MicOffIcon /> : <MicIcon />}
                  onPointerDown={handlePointerDown}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerCancel}
                  className={classes.recordButton}
                  style={{ touchAction: 'none', userSelect: 'none' }}
                >
                  {isRecording ? 'DETENER GRABACIÓN' : 'MANTENER PARA HABLAR'}
                </Button>
                <Tooltip title="Si está activo, al soltar el botón se procesará el pedido automáticamente con el Asistente AI">
                  <FormControlLabel
                    control={
                      <Switch
                        id="ai-assistant-auto-process-switch"
                        size="small"
                        checked={autoProcess}
                        onChange={(e) => setAutoProcess(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="caption" style={{ fontWeight: 500, fontSize: '0.8rem' }}>
                        Auto-procesar al soltar
                      </Typography>
                    }
                    style={{ margin: 0 }}
                  />
                </Tooltip>
              </Row>
              <Typography variant="caption" color="textSecondary" className={classes.inputLabel}>
                {isRecording
                  ? isHoldingRef.current
                    ? 'Soltá para finalizar'
                    : 'Grabando... clic para detener'
                  : 'Mantené presionado o clic para manos libres'}
              </Typography>
            </Row>
            <TextField
              id="ai-assistant-input"
              multiline
              minRows={2}
              maxRows={4}
              fullWidth
              variant="outlined"
              placeholder="Escribe o dicta el pedido (ej: Gomez Cuenta 15 quiere 5 baldes 15w40 al contado)..."
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              disabled={loading}
            />
            {/* Dynamic recurring sales suggestions (only shown if real recurring orders exist) */}
            {/* {suggestions && suggestions.length > 0 && (
              <Column vertical="center" className={classes.suggestionsRow}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  className={classes.suggestionsLabel}
                >
                  Sugerencias de pedidos habituales recientes:
                </Typography>
                {suggestions.map((sug, idx) => (
                  <Chip
                    key={idx}
                    label={`${sug.clientName}: ${sug.quantity}x ${sug.productName}`}
                    size="small"
                    onClick={() => handleSendMessage(sug.prompt)}
                    clickable
                    color="primary"
                    variant="filled"
                    className={classes.suggestionChip}
                  />
                ))}
              </Column>
            )} */}
          </>
        )}
      </DialogContent>

      {!loading && (
        <DialogActions className={classes.dialogActions}>
          <Button
            id="ai-assistant-cancel"
            variant="contained"
            color="error"
            onClick={handleClose}
            disabled={actionLoading}
          >
            Cancelar
          </Button>
          <Button
            id="ai-assistant-send"
            variant="contained"
            color="primary"
            endIcon={<SendIcon />}
            onClick={() => handleSendMessage()}
            disabled={!inputText.trim()}
          >
            Procesar Pedido
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

AiSellAssistantModal.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
  onDraftHydrate: func,
  opesi: bool,
  isQuoteRequest: bool,
  opesiTaxPercentageSelected: object
};

export default AiSellAssistantModal;
